import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Supplier } from '../models/supplier.model';
import { environment } from '../../../environments/environment';
import { DocumentsService } from '../../core/services/documents.service';

@Injectable({
  providedIn: 'root',
})
export class SupplierRegistryService {
  private baseUrl = `${environment.backofficeApiUrl}/api`;

  constructor(
    private http: HttpClient,
    private documentsService: DocumentsService
  ) {}
  // =========================
  // GET - Get all suppliers
  // =========================
  getSuppliers(excludeDraft: boolean = false): Observable<any> {
    const params: any = {};
    if (excludeDraft) {
      params.excludeDraft = 'true';
    }
    return this.http.get(`${this.baseUrl}/v1/suppliers`, { params });
  }

  // =========================
  // POST - Create supplier
  // =========================
  createSupplier(supplier: Supplier): Observable<Supplier> {
    return this.http.post<Supplier>(`${this.baseUrl}/v1/suppliers`, supplier, {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  // =========================
  // GET - Get supplier by id
  // =========================
  getSupplier(id: string): Observable<Supplier> {
    return this.http.get<Supplier>(`${this.baseUrl}/v1/suppliers/${id}`);
  }

  // =========================
  // DELETE - Delete supplier
  // =========================
  deleteSupplier(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/v1/suppliers/${id}`);
  }

  // =========================
  // PATCH - Partial update
  // =========================
  updateSupplierPatch(id: string, payload: Partial<Supplier>): Observable<Supplier> {
    return this.http.patch<Supplier>(`${this.baseUrl}/v1/suppliers/${id}`, payload);
  }

  // =========================
  // PUT - Full update
  // =========================
  updateSupplierPut(id: string, supplier: Supplier): Observable<Supplier> {
    return this.http.put<Supplier>(`${this.baseUrl}/v1/suppliers/${id}`, supplier);
  }

  // =========================
  // POST - Update supplier details
  // =========================
  updateSupplierDetails(supplierId: string, details: any): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/v1/suppliers/${supplierId}/details`, details, {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  // =========================
  // UTILITY - Map supplier data for display
  // =========================
  /**
   * Mappa i dati del fornitore per la visualizzazione nella tabella
   * Centralizza la logica per evitare duplicazioni nei componenti
   */
  mapSuppliersForDisplay(suppliers: any[]): any[] {
    return suppliers.map((supplier: any) => ({
      ...supplier,
      vatNumber: supplier.vatNumber ? `${supplier.vatCountry ?? ''}${supplier.vatNumber}` : '',
      contactPerson: supplier.axaBuyer, // Alias: la tabella mostra axaBuyer nella colonna "Buyer"
      createdAt: supplier.createdAt ? new Date(supplier.createdAt).toLocaleDateString('it-IT') : '',
      statusDisplay: this.getStatusDisplay(supplier.status),
    }));
  }

  /**
   * Converte lo status del fornitore in formato leggibile
   */
  getStatusDisplay(status: string): string {
    switch (status) {
      case 'DRAFT':
        return 'BOZZA';
      case 'ACTIVE':
        return 'ATTIVO';
      case 'CEASED':
        return 'CESSATO';
      case 'INACTIVE':
        return 'INATTIVO';
      default:
        return status;
    }
  }

    /**
     * Export fornitori in Excel
     */
  exportSuppliersRegistry(supplierId?: number | null): Observable<HttpResponse<Blob>> {
    return this.documentsService.exportRegistry('SUP_REF_01', supplierId || null, null, null);
  }
}
